// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadAccountSummary, { defaultParameters } from "@steiner/App/Handlers/XNG/AccountSummary/LoadAccountSummary";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React, { useEffect } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    return {
        accountSummary: state.xAccountSummary.accountSummary,
        isLoading: state.xAccountSummary.isLoading,
        language: state.context.session.language,
        currencySymbol: state.context.session.currency?.currencySymbol ?? "",
    };
};

const mapDispatchToProps = {
    loadAccountSummary,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSummaryStyles {
    container?: InjectableCss;
    customerSectionTitle?: TypographyPresentationProps;
    openBalancesSectionTitle?: TypographyPresentationProps;
    customerInfoSection?: InjectableCss;
    openBalancesInfoSection?: InjectableCss;
    lineWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    totalLineWrapper?: TypographyPresentationProps;
    totalValue?: TypographyPresentationProps;
}

export const accountSummaryStyles: AccountSummaryStyles = {};

const styles = accountSummaryStyles;
export const AccountSummary: FC<Props> = ({
    accountSummary,
    loadAccountSummary,
    isLoading,
    language,
    currencySymbol,
}) => {
    useEffect(() => {
        loadAccountSummary({ ...defaultParameters });
    }, []);

    if (!accountSummary || !language) {
        return null;
    }

    const getCurrencyDisplay = (value: string) => {
        return getLocalizedCurrency({
            amount: Number(value),
            language,
            currencySymbol,
        });
    };

    return (
        <LoadingOverlay data-test-selector="accountSummary_Loading" loading={isLoading}>
            <StyledWrapper {...styles.container}>
                <Typography {...styles.customerSectionTitle}>{accountSummary.customerName}</Typography>
                <Typography {...styles.openBalancesSectionTitle}>{translate("Open Balances")}</Typography>
                <StyledWrapper {...styles.customerInfoSection}>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Terms")}:</Typography>
                        <Typography {...styles.value}>{accountSummary.terms}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Credit Limit")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.creditLimit)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Available Credit")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.availableCredit)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Last Purchase")}:</Typography>
                        <Typography {...styles.value}>{accountSummary.lastPurchase}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Last Payment")}:</Typography>
                        <Typography {...styles.value}>{accountSummary.lastPayment}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("MTD Purchases")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.mtdPurchases)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("YTD Purchases")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.ytdPurchases)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Payment Days")}:</Typography>
                        <Typography {...styles.value}>{accountSummary.paymentDays}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.openBalancesInfoSection}>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Future")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.future)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Current")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.current)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("31-60 Days")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.days31To60)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("61-90 Days")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.days61To90)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("91-120 Days")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.days91To120)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.lineWrapper}>
                        <Typography {...styles.label}>{translate("Over 120 Days")}:</Typography>
                        <Typography {...styles.value}>
                            {getCurrencyDisplay(accountSummary.daysOver120)}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.totalLineWrapper}>
                        <Typography {...styles.label}>{translate("Total")}:</Typography>
                        <Typography {...styles.totalValue}>
                            {getCurrencyDisplay(accountSummary.total)}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSummary),
    definition: {
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["AccountSummaryPage", "InvoiceHistoryPage"],
    },
};

export default widgetModule;
